/* istanbul ignore file */
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Body1,
  Caption1Strong,
  Input,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  typographyStyles,
} from '@fluentui/react-components';
import { ColorPalette } from '@bingads-webui-campaign-react/ads-color-picker';

const useColorPickerStyles = makeStyles({
  root: {
    marginTop: tokens.spacingVerticalS,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalSNudge,
  },
  label: {
    display: 'inline-block',
    marginBottom: tokens.spacingVerticalM,
  },
  colorPreview: {
    width: '36px',
    height: '36px',
    ...shorthands.padding(
      tokens.spacingVerticalXXS,
      tokens.spacingHorizontalXXS
    ),
    ...shorthands.borderStyle('solid'),
    ...shorthands.borderWidth(tokens.strokeWidthThin),
    ...shorthands.borderColor(tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
  },
  colorInput: {
    ...shorthands.flex(1),
    ...typographyStyles.body1,
  },
});

export const ColorPicker = ({
  i18n,
  label,
  color,
  className,
  onChangeColor,
}) => {
  const classes = useColorPickerStyles();
  const labelId = label || 'color-picker-name';
  const [colorState, setColorState] = React.useState(color);

  useEffect(() => {
    setColorState(color);
  }, [color]);

  const onUpdateColor = useCallback((_name, value) => {
    const colorValue = value ? value.hex : `#${_name}`;
    onChangeColor(colorValue);
  }, [onChangeColor]);

  const onChange = useCallback((_e, data) => {
    setColorState(`#${data.value}`);
    // update color if it's valid hex code
    if (/^[0-9A-Fa-f]{6}$/.test(data.value)) {
      onChangeColor(`#${data.value}`);
    }
  }, [onChangeColor]);

  return (
    <div
      className={mergeClasses(
        'display-ads-color-picker',
        classes.root,
        className
      )}
    >
      {label && (
        <Caption1Strong className={classes.label} aria-label={label}>
          {label}
        </Caption1Strong>
      )}
      <div className={classes.container}>
        <ColorPalette
          showTooltipOnSelect
          i18n={i18n}
          key={labelId}
          colorData={{
            Name: labelId,
            Color: color,
            HexCode: `#${color.replace('#', '')}`,
          }}
          isDuplicate
          hideCaption
          palletteBoxSize="44px"
          paletteColorSize="36px"
          updateColor={onUpdateColor}
        />
        <Input
          maxLength={6}
          defaultValue={colorState.replace('#', '')}
          value={colorState.replace('#', '')}
          aria-labelledby="color-picker"
          contentBefore={<Body1 size={400}>#</Body1>}
          onChange={onChange}
          className={mergeClasses('color-input', classes.colorInput)}
        />
      </div>
    </div>
  );
};

ColorPicker.propTypes = {
  i18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string.isRequired,
  onChangeColor: PropTypes.func,
};

ColorPicker.defaultProps = {
  label: null,
  className: '',
  onChangeColor: () => {},
};
